import React, { useState, useEffect } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import { Box, Paper, Typography, Button, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';

const ScatterPlot = ({ plotData, defaultTitle, xlabel, ylabel, titleKey, xColumn, yColumn, colorColumn, showTable = true }) => {
  const plotRef = React.useRef(null);
  const [title, setTitle] = React.useState(() => localStorage.getItem(`mult_title_${titleKey}`) || defaultTitle || '');
  const [xLabel, setXLabel] = React.useState(() => localStorage.getItem(`mult_xlabel_${titleKey}`) || xlabel || '');
  const [yLabel, setYLabel] = React.useState(() => localStorage.getItem(`mult_ylabel_${titleKey}`) || ylabel || '');
  const [showEditor, setShowEditor] = React.useState(false);
  const [showDataTable, setShowDataTable] = React.useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const groups = {};
    plotData.forEach(point => {
      const colorValue = point[colorColumn];
      const key = String(colorValue);
      if (!groups[key]) groups[key] = { x: [], y: [], name: key };
      groups[key].x.push(point[xColumn]);
      groups[key].y.push(point[yColumn]);
    });
    const traces = Object.keys(groups).map(key => ({
      x: groups[key].x, y: groups[key].y, type: 'scatter', mode: 'markers',
      name: groups[key].name, marker: { size: 8, opacity: 0.7 }
    }));
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t, xaxis: { title: xl }, yaxis: { title: yl },
      template: 'plotly_white', height: 400,
      legend: { orientation: 'h', y: -0.2 },
      margin: { t: 40, b: 80, l: 60, r: 20 }
    }, { responsive: true });
  };

  React.useEffect(() => {
    if (plotData && plotData.length > 0) renderPlot(title, xLabel, yLabel);
  }, [plotData, title, xLabel, yLabel]);

  const applyTitles = () => {
    renderPlot(title, xLabel, yLabel);
    localStorage.setItem(`mult_title_${titleKey}`, title);
    localStorage.setItem(`mult_xlabel_${titleKey}`, xLabel);
    localStorage.setItem(`mult_ylabel_${titleKey}`, yLabel);
    setShowEditor(false);
  };

  if (!plotData || plotData.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>No data available. Run analysis first.</div>;
  }
  
  const tableData = plotData.slice(0, 100);
  const tableHeaders = Object.keys(tableData[0] || {});
  
  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>{title}</h4>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => setShowDataTable(!showDataTable)} style={{ padding: '4px 10px', fontSize: '10px', background: '#34495e', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
            {showDataTable ? 'Hide Table' : 'Show Table'}
          </button>
          <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '4px 10px', fontSize: '10px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '3px', cursor: 'pointer' }}>✏️ Edit</button>
        </div>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, fontSize: '11px', padding: '4px' }} />
          <input placeholder="X axis" value={xLabel} onChange={e => setXLabel(e.target.value)} style={{ flex: 1, fontSize: '11px', padding: '4px' }} />
          <input placeholder="Y axis" value={yLabel} onChange={e => setYLabel(e.target.value)} style={{ flex: 1, fontSize: '11px', padding: '4px' }} />
          <button onClick={applyTitles} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', fontSize: '10px' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '400px' }}></div>
      {showDataTable && (
        <div style={{ marginTop: '12px' }}>
          <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                <tr>{tableHeaders.map(h => <th key={h} style={{ padding: '6px' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    {tableHeaders.map(h => <td key={h} style={{ padding: '4px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const LineChart = ({ plotData, defaultTitle, xlabel, ylabel, titleKey, showTable = true }) => {
  const plotRef = React.useRef(null);
  const [title, setTitle] = React.useState(() => localStorage.getItem(`mult_line_title_${titleKey}`) || defaultTitle || '');
  const [xLabel, setXLabel] = React.useState(() => localStorage.getItem(`mult_line_xlabel_${titleKey}`) || xlabel || '');
  const [yLabel, setYLabel] = React.useState(() => localStorage.getItem(`mult_line_ylabel_${titleKey}`) || ylabel || '');
  const [showEditor, setShowEditor] = React.useState(false);
  const [showDataTable, setShowDataTable] = React.useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const traces = plotData.map(series => ({
      x: series.x, y: series.y, type: 'scatter', mode: 'lines+markers',
      name: series.name, line: { width: 2 }, marker: { size: 5 }
    }));
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t, xaxis: { title: xl }, yaxis: { title: yl },
      template: 'plotly_white', height: 400,
      legend: { orientation: 'h', y: -0.2 },
      margin: { t: 40, b: 80, l: 60, r: 20 }
    }, { responsive: true });
  };

  React.useEffect(() => {
    if (plotData && plotData.length > 0) renderPlot(title, xLabel, yLabel);
  }, [plotData, title, xLabel, yLabel]);

  const applyTitles = () => {
    renderPlot(title, xLabel, yLabel);
    localStorage.setItem(`mult_line_title_${titleKey}`, title);
    localStorage.setItem(`mult_line_xlabel_${titleKey}`, xLabel);
    localStorage.setItem(`mult_line_ylabel_${titleKey}`, yLabel);
    setShowEditor(false);
  };

  if (!plotData || plotData.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>No data available. Run analysis first.</div>;
  }
  
  const tableData = []; plotData.forEach(series => { for (let i = 0; i < series.x.length; i++) { tableData.push({ name: series.name, x: series.x[i], y: series.y[i] }); } });
  const tableHeaders = ['name', 'x', 'y'];
  
  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>{title}</h4>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => setShowDataTable(!showDataTable)} style={{ padding: '4px 10px', fontSize: '10px', background: '#34495e', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
            {showDataTable ? 'Hide Table' : 'Show Table'}
          </button>
          <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '4px 10px', fontSize: '10px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '3px', cursor: 'pointer' }}>✏️ Edit</button>
        </div>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, fontSize: '11px', padding: '4px' }} />
          <input placeholder="X axis" value={xLabel} onChange={e => setXLabel(e.target.value)} style={{ flex: 1, fontSize: '11px', padding: '4px' }} />
          <input placeholder="Y axis" value={yLabel} onChange={e => setYLabel(e.target.value)} style={{ flex: 1, fontSize: '11px', padding: '4px' }} />
          <button onClick={applyTitles} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', fontSize: '10px' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '400px' }}></div>
      {showDataTable && (
        <div style={{ marginTop: '12px' }}>
          <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                <tr>{tableHeaders.map(h => <th key={h} style={{ padding: '6px' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '4px' }}>{row.name}</td>
                    <td style={{ padding: '4px' }}>{row.x}</td>
                    <td style={{ padding: '4px' }}>{typeof row.y === 'number' ? row.y.toFixed(4) : row.y}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const TableComponent = ({ data, title }) => {
  const [showTable, setShowTable] = React.useState(true);
  if (!data || data.length === 0) return null;
  const headers = Object.keys(data[0]);
  return (
    <div style={{ marginTop: '16px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ fontSize: '13px', margin: 0 }}>{title}</h4>
        <button onClick={() => setShowTable(!showTable)} style={{ padding: '4px 10px', fontSize: '10px', background: '#34495e', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>{showTable ? 'Hide' : 'Show'}</button>
      </div>
      {showTable && (
        <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
              <tr>{headers.map(h => <th key={h} style={{ padding: '6px' }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  {headers.map(h => <td key={h} style={{ padding: '4px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MultiplicativeModel = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [descriptiveData, setDescriptiveData] = useState(null);
  const [retainedResults, setRetainedResults] = useState(null);
  const [bootstrapResults, setBootstrapResults] = useState(null);
  const [projectionResults, setProjectionResults] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedGradeProjection, setSelectedGradeProjection] = useState(null);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [exclusionColumn, setExclusionColumn] = useState(null);
  const [exclusionValues, setExclusionValues] = useState([]);
  const [exclusionOptions, setExclusionOptions] = useState([]);
  const [bootstrapIterations, setBootstrapIterations] = useState(500);
  const [confidenceLevel, setConfidenceLevel] = useState(75);
  const [saveColNameMean, setSaveColNameMean] = useState('projection_mean');
  const [saveColName75, setSaveColName75] = useState(`projection_${75}`);
  const [groups, setGroups] = useState({ facility: false, dates: false, calibration: false, status: false, target: false, mrp: false });
  const [formData, setFormData] = useState({ Facility_ID: null, Default_Date: null, Calibration_segment: null, Grade: null, Status: null, Time_in_default: null, Target_variable: null, MRP_column: null, observed_LGD: null });

  // ========== ADDED: Analysis state variables ==========
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [allDatasets, setAllDatasets] = useState([]);

  // ========== ADDED: Mode detection ==========
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  // ========== ADDED: Load saved analysis on mount ==========
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    }
  }, []);

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setAllDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        rows: ds.row_count,
        columns: ds.column_count
      }));
      setAllDatasets(datasets);
    } catch (err) {
      console.error('Failed to load datasets', err);
    }
  };
  useEffect(() => {
    fetchDatasets();
  }, []);

  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo({
      id: data.id,
      name: `Dataset ${data.id}`,
      rows: data.rows,
      columns: data.columns
    });
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  // Update name when allDatasets is loaded
  useEffect(() => {
    if (datasetInfo && allDatasets.length > 0) {
      const datasetFromList = allDatasets.find(d => d.id === datasetInfo.id);
      if (datasetFromList && datasetFromList.name !== datasetInfo.name) {
        setDatasetInfo(prev => ({ ...prev, name: datasetFromList.name }));
      }
    }
  }, [allDatasets, datasetInfo]);

  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const loadStatusValues = async () => {
    if (!formData.Status || !datasetInfo?.id) return;
    try { 
      const res = await api.get(`/api/cart/column-values?dataset_id=${datasetInfo.id}&column=${formData.Status.value}`); 
      setStatusOptions(res.data.values.map(v => ({ value: v, label: String(v) }))); 
    } catch (err) { console.error(err); }
  };
  useEffect(() => { if (formData.Status) loadStatusValues(); }, [formData.Status, datasetInfo]);

  const loadExclusionValues = async () => {
    if (!exclusionColumn || !datasetInfo?.id) return;
    try { 
      const res = await api.get(`/api/cart/column-values?dataset_id=${datasetInfo.id}&column=${exclusionColumn.value}`); 
      setExclusionOptions(res.data.values.map(v => ({ value: v, label: String(v) }))); 
    } catch (err) { console.error(err); }
  };
  useEffect(() => { if (exclusionColumn) loadExclusionValues(); }, [exclusionColumn, datasetInfo]);

  const toggleGroup = (group) => {
    setGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // ========== ADDED: Load saved analysis function ==========
  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        if (analysis.input_parameters.dataset_id) {
          setDatasetInfo({
            id: analysis.input_parameters.dataset_id,
            name: analysis.input_parameters.dataset_name,
            rows: analysis.input_parameters.rows,
            columns: analysis.input_parameters.columns
          });
          setDatasetLoaded(true);
          setColumns(analysis.input_parameters.columns || []);
        }
        
        setFormData({
          Facility_ID: analysis.input_parameters.Facility_ID ? { value: analysis.input_parameters.Facility_ID, label: analysis.input_parameters.Facility_ID } : null,
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          Calibration_segment: analysis.input_parameters.Calibration_segment ? { value: analysis.input_parameters.Calibration_segment, label: analysis.input_parameters.Calibration_segment } : null,
          Grade: analysis.input_parameters.Grade ? { value: analysis.input_parameters.Grade, label: analysis.input_parameters.Grade } : null,
          Status: analysis.input_parameters.Status ? { value: analysis.input_parameters.Status, label: analysis.input_parameters.Status } : null,
          Time_in_default: analysis.input_parameters.Time_in_default ? { value: analysis.input_parameters.Time_in_default, label: analysis.input_parameters.Time_in_default } : null,
          Target_variable: analysis.input_parameters.Target_variable ? { value: analysis.input_parameters.Target_variable, label: analysis.input_parameters.Target_variable } : null,
          MRP_column: analysis.input_parameters.MRP_column ? { value: analysis.input_parameters.MRP_column, label: analysis.input_parameters.MRP_column } : null,
          observed_LGD: analysis.input_parameters.observed_LGD ? { value: analysis.input_parameters.observed_LGD, label: analysis.input_parameters.observed_LGD } : null
        });
        
        if (analysis.input_parameters.Status_values) {
          setSelectedStatus(analysis.input_parameters.Status_values.map(v => ({ value: v, label: String(v) })));
        }
        
        if (analysis.input_parameters.exclusionColumn) {
          setExclusionColumn({ value: analysis.input_parameters.exclusionColumn, label: analysis.input_parameters.exclusionColumn });
        }
        if (analysis.input_parameters.exclusionValues) {
          setExclusionValues(analysis.input_parameters.exclusionValues.map(v => ({ value: v, label: String(v) })));
        }
        
        if (analysis.input_parameters.bootstrapIterations) setBootstrapIterations(analysis.input_parameters.bootstrapIterations);
        if (analysis.input_parameters.confidenceLevel) setConfidenceLevel(analysis.input_parameters.confidenceLevel);
        
        if (analysis.input_parameters.descriptiveData) setDescriptiveData(analysis.input_parameters.descriptiveData);
        if (analysis.input_parameters.retainedResults) setRetainedResults(analysis.input_parameters.retainedResults);
        if (analysis.input_parameters.bootstrapResults) setBootstrapResults(analysis.input_parameters.bootstrapResults);
        if (analysis.input_parameters.projectionResults) setProjectionResults(analysis.input_parameters.projectionResults);
        if (analysis.input_parameters.gradeOptions) setGradeOptions(analysis.input_parameters.gradeOptions);
      }
      
      if (analysis.output_result) {
        setStep(5);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Continue to run analysis steps.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  // ========== ADDED: Save Analysis function ==========
  const handleSaveAnalysis = async () => {
    if (!datasetInfo) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'multiplicative_projection',
        description: analysisDescription,
        input_parameters: {
          dataset_id: datasetInfo.id,
          dataset_name: datasetInfo.name,
          rows: datasetInfo.rows,
          columns: datasetInfo.columns,
          Facility_ID: formData.Facility_ID?.value || '',
          Default_Date: formData.Default_Date?.value || '',
          Calibration_segment: formData.Calibration_segment?.value || '',
          Grade: formData.Grade?.value || '',
          Status: formData.Status?.value || '',
          Time_in_default: formData.Time_in_default?.value || '',
          Target_variable: formData.Target_variable?.value || '',
          MRP_column: formData.MRP_column?.value || '',
          observed_LGD: formData.observed_LGD?.value || '',
          Status_values: selectedStatus.map(s => s.value),
          exclusionColumn: exclusionColumn?.value || '',
          exclusionValues: exclusionValues.map(v => v.value),
          bootstrapIterations: bootstrapIterations,
          confidenceLevel: confidenceLevel,
          descriptiveData: descriptiveData,
          retainedResults: retainedResults,
          bootstrapResults: bootstrapResults,
          projectionResults: projectionResults,
          gradeOptions: gradeOptions
        },
        output_result: {
          descriptiveData: descriptiveData,
          retainedResults: retainedResults,
          bootstrapResults: bootstrapResults,
          projectionResults: projectionResults
        },
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  // ========== ADDED: Update Metadata function ==========
  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // ========== ADDED: Add Comment function ==========
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const saveProjection = async (projectionType) => {
    const colName = projectionType === 'mean' ? saveColNameMean : saveColName75;
    if (!colName || !colName.trim()) { toast.error('Please enter a column name'); return; }
    setLoadingStep('save' + projectionType);
    try {
      const response = await api.post('/api/multiplicative-projection/save-projection', { column_name: colName.trim(), projection_type: projectionType });
      toast.success(response.data.message || `Projection saved as '${colName}'`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save projection'); }
    finally { setLoadingStep(null); }
  };

  const runStep0 = async () => {
    const required = ['Facility_ID', 'Default_Date', 'Status', 'Time_in_default', 'Target_variable', 'MRP_column'];
    for (let f of required) { if (!formData[f]) { toast.error(`Please select ${f}`); return; } }
    if (!formData.Grade && !formData.Calibration_segment) { toast.error('Please select either Grade or Calibration segment (or both)'); return; }
    if (selectedStatus.length === 0) { toast.error('Please select at least one value for status filter'); return; }
    setLoading(true);
    try {
      await api.post('/api/multiplicative-projection/step0', { ...formData, Facility_ID: formData.Facility_ID.value, Default_Date: formData.Default_Date.value, Calibration_segment: formData.Calibration_segment?.value || '', Grade: formData.Grade?.value || '', Status: formData.Status.value, Time_in_default: formData.Time_in_default.value, Target_variable: formData.Target_variable.value, MRP_column: formData.MRP_column.value, Status_values: selectedStatus.map(s => s.value), observed_LGD: formData.observed_LGD?.value || '' });
      toast.success('Parameters saved');
      setStep(1);
    } catch (err) { toast.error('Failed to save parameters'); }
    finally { setLoading(false); }
  };

  const runDescriptive = async () => {
    setLoadingStep('descriptive');
    try {
      const response = await api.post('/api/multiplicative-projection/step1', { ...formData, Facility_ID: formData.Facility_ID.value, Default_Date: formData.Default_Date.value, Calibration_segment: formData.Calibration_segment?.value || '', Grade: formData.Grade?.value || '', Status: formData.Status.value, Time_in_default: formData.Time_in_default.value, Target_variable: formData.Target_variable.value, MRP_column: formData.MRP_column.value, Status_values: selectedStatus.map(s => s.value), observed_LGD: formData.observed_LGD?.value || '' });
      setDescriptiveData(response.data.results);
      setGradeOptions((response.data.results?.meta?.grades || []).map(g => ({ value: g, label: String(g) })));
      toast.success(response.data.message);
      setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || 'Descriptive analysis failed'); }
    finally { setLoadingStep(null); }
  };

  const runRetained = async () => {
    setLoadingStep('retained');
    try {
      const response = await api.post('/api/multiplicative-projection/step2', { exclusion_var: exclusionColumn?.value || '', exclusion_values: exclusionValues.map(v => v.value) });
      setRetainedResults(response.data.results);
      toast.success(response.data.message);
      setStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || 'Retained analysis failed'); }
    finally { setLoadingStep(null); }
  };

  const runBootstrap = async () => {
    setLoadingStep('bootstrap');
    try {
      const response = await api.post('/api/multiplicative-projection/step3', { n_bootstrap: bootstrapIterations, Quantile_MoC: confidenceLevel });
      setBootstrapResults(response.data.results);
      toast.success(response.data.message);
      setStep(4);
    } catch (err) { toast.error(err.response?.data?.detail || 'Bootstrap failed'); }
    finally { setLoadingStep(null); }
  };

  const runProjection = async () => {
    setLoadingStep('projection');
    try {
      const response = await api.post('/api/multiplicative-projection/step4', {});
      setProjectionResults(response.data.results);
      toast.success(response.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Projection failed'); }
    finally { setLoadingStep(null); }
  };

  const exportResults = async () => {
    try {
      const customTitles = {};
      ['descriptive', 'retained', 'bootstrap', 'projection'].forEach(key => {
        const t = localStorage.getItem(`mult_title_${key}`);
        const x = localStorage.getItem(`mult_xlabel_${key}`);
        const y = localStorage.getItem(`mult_ylabel_${key}`);
        const lt = localStorage.getItem(`mult_line_title_${key}`);
        const lx = localStorage.getItem(`mult_line_xlabel_${key}`);
        const ly = localStorage.getItem(`mult_line_ylabel_${key}`);
        if (t) customTitles[`title_${key}`] = t;
        if (x) customTitles[`xlabel_${key}`] = x;
        if (y) customTitles[`ylabel_${key}`] = y;
        if (lt) customTitles[`line_title_${key}`] = lt;
        if (lx) customTitles[`line_xlabel_${key}`] = lx;
        if (ly) customTitles[`line_ylabel_${key}`] = ly;
      });
      const response = await api.post('/api/multiplicative-projection/export-with-titles', customTitles, { responseType: 'blob' })
        .catch(() => api.get('/api/multiplicative-projection/export-full', { responseType: 'blob' }));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'multiplicative_projection_results.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to export results'); }
  };

  const sc = (t) => {
    if (t === 0) setStep(0);
    else if (t === 1 && datasetLoaded) setStep(1);
    else if (t === 2 && descriptiveData) setStep(2);
    else if (t === 3 && retainedResults) setStep(3);
    else if (t === 4 && bootstrapResults) setStep(4);
    else if (t === 5 && projectionResults) setStep(5);
  };

  const cs = { control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }), valueContainer: (b) => ({ ...b, padding: '0 8px' }), placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }), menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };
  const compactCs = { control: (b) => ({ ...b, minHeight: '28px', fontSize: '11px', borderColor: '#e0e0e0' }), valueContainer: (b) => ({ ...b, padding: '0 4px' }), placeholder: (b) => ({ ...b, fontSize: '11px', color: '#95a5a6' }), menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };

  const descriptiveScatterData = descriptiveData?.scatter_data || [];
  const filteredDescriptiveData = selectedGrade && selectedGrade !== 'all' 
    ? descriptiveScatterData.filter(d => d.grade === selectedGrade) 
    : descriptiveScatterData;
  const retainedScatterData = retainedResults?.scatter_data || [];
  const bootstrapScatterData = bootstrapResults?.scatter_data || [];

  const observedData = projectionResults?.observed_data || [];
  const projectedData = projectionResults?.projected_data || [];
  const projectedData75 = projectionResults?.projected_data_75 || [];
  
  const projectionTraces = [];
  const allGrades = [...new Set([...observedData.map(d => d.grade), ...projectedData.map(d => d.grade)])];
  
  allGrades.forEach(grade => {
    const gradeObserved = observedData.filter(d => d.grade === grade);
    if (gradeObserved.length > 0) {
      projectionTraces.push({ name: `${grade} - Observed`, x: gradeObserved.map(d => d.year), y: gradeObserved.map(d => d.recovery) });
    }
    const gradeProj = projectedData.filter(d => d.grade === grade);
    if (gradeProj.length > 0 && gradeObserved.length > 0) {
      projectionTraces.push({ name: `${grade} - Proj Mean`, x: gradeObserved.map(d => d.year), y: gradeObserved.map(() => gradeProj[0]?.recovery || 0) });
    }
    const gradeProj75 = projectedData75.filter(d => d.grade === grade);
    if (gradeProj75.length > 0 && gradeObserved.length > 0) {
      projectionTraces.push({ name: `${grade} - Proj ${confidenceLevel}th`, x: gradeObserved.map(d => d.year), y: gradeObserved.map(() => gradeProj75[0]?.recovery || 0) });
    }
  });
  
  const filteredTraces = selectedGradeProjection && selectedGradeProjection !== 'all' 
    ? projectionTraces.filter(t => t.name.includes(selectedGradeProjection)) 
    : projectionTraces;

  const stepTabs = ['Select', 'Review', 'Descriptive', 'Retained', 'Bootstrap', 'Projection'];
  const buttonStyle = { padding: '8px 20px', fontSize: '13px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
  const groupStyle = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '12px', overflow: 'visible', position: 'relative' };
  const groupHeaderStyle = { background: '#f5f7fa', padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '13px', color: '#2c3e50', opacity: isViewMode ? 0.6 : 1 };
  const groupContentStyle = { padding: '12px', background: 'white', overflow: 'visible' };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Multiplicative Projection Model</h1>
      
      {/* ========== ADDED: View mode banner ========== */}
      {isViewMode && projectionResults && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px', flexWrap: 'wrap' }}>
        {stepTabs.map((tab, idx) => (
          <button key={idx} onClick={() => sc(idx)}
            style={{ padding: '6px 12px', fontSize: '11px', background: step === idx ? '#1abc9c' : 'transparent', color: step === idx ? 'white' : '#7f8c8d', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{tab}</button>
        ))}
      </div>

      {step === 0 && (<>
        <DatasetSelector projectId={new URLSearchParams(window.location.search).get("project_id")} apiPrefix="/api/multiplicative-projection" onDatasetLoaded={hdl} />
        
        {/* Show dataset name when a dataset is loaded */}
        {datasetInfo && (
          <div style={{ 
            marginTop: '16px', 
            marginBottom: '16px', 
            padding: '12px', 
            background: '#e8f8f5', 
            borderRadius: '8px',
            borderLeft: '4px solid #1abc9c'
          }}>
            <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
              📌 Currently selected dataset:
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
              {datasetInfo.name}
            </div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
              {datasetInfo.rows?.toLocaleString()} rows • {datasetInfo.columns?.length || 0} columns
            </div>
          </div>
        )}

        {columns.length > 0 && (<div style={{ marginTop: '16px' }}>
          {[
            ['facility', '🏢 1. Facility identifier', <div style={{ maxWidth: '400px' }}><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Facility ID *</label><Select options={columnOptions} value={formData.Facility_ID} onChange={v => hfc('Facility_ID', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select facility ID column..." isDisabled={isViewMode}/></div>],
            ['dates', '📅 2. Key dates', <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Default Date *</label><Select options={columnOptions} value={formData.Default_Date} onChange={v => hfc('Default_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select default date column..." isDisabled={isViewMode}/></div><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Time in Default *</label><Select options={columnOptions} value={formData.Time_in_default} onChange={v => hfc('Time_in_default', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select time in default column..." isDisabled={isViewMode}/></div></div>],
            ['calibration', '🎯 3. Calibration & Grade', <div><div style={{ marginBottom: '12px' }}><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Calibration Segment (optional)</label><Select options={columnOptions} value={formData.Calibration_segment} onChange={v => hfc('Calibration_segment', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select calibration segment..." isDisabled={isViewMode}/></div><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Grade (optional)</label><Select options={columnOptions} value={formData.Grade} onChange={v => hfc('Grade', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select grade column..." isDisabled={isViewMode}/></div><p style={{ fontSize: '10px', color: '#e74c3c', marginTop: '8px' }}>⚠️ At least one required</p></div>],
            ['status', '⚠️ 4. Status filter', <div><div style={{ marginBottom: '12px' }}><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Status Column *</label><Select options={columnOptions} value={formData.Status} onChange={v => hfc('Status', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select status column..." isDisabled={isViewMode}/></div>{formData.Status && statusOptions.length > 0 && <div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Status Values *</label><Select options={statusOptions} isMulti value={selectedStatus} onChange={setSelectedStatus} styles={cs} menuPortalTarget={document.body} placeholder="Select status values..." isDisabled={isViewMode}/></div>}</div>],
            ['target', '📊 5. Target variable', <div><div style={{ marginBottom: '12px' }}><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Target Variable *</label><Select options={columnOptions} value={formData.Target_variable} onChange={v => hfc('Target_variable', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select target variable..." isDisabled={isViewMode}/></div><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Observed LGD (optional)</label><Select options={columnOptions} value={formData.observed_LGD} onChange={v => hfc('observed_LGD', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select observed LGD..." isDisabled={isViewMode}/></div></div>],
            ['mrp', '⏱️ 6. MRP', <div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>MRP Column *</label><Select options={columnOptions} value={formData.MRP_column} onChange={v => hfc('MRP_column', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select MRP column..." isDisabled={isViewMode}/></div>]
          ].map(([key, title, content]) => (
            <div key={key} style={groupStyle}>
              <div style={groupHeaderStyle} onClick={() => toggleGroup(key)}><span>{title}</span><span>{groups[key] ? '−' : '+'}</span></div>
              {groups[key] && <div style={groupContentStyle}>{content}</div>}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}><button onClick={runStep0} disabled={loading || isViewMode} style={{ ...buttonStyle, background: loading || isViewMode ? '#95a5a6' : '#1abc9c' }}>Save & Continue →</button></div>
        </div>)}
      </>)}

      {step === 1 && (<div>
        <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}><h3 style={{ marginBottom: '12px', fontSize: '14px' }}>Review Parameters</h3><div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px', fontSize: '12px' }}><p><strong>Facility ID:</strong> {formData.Facility_ID?.label || '-'}</p><p><strong>Default Date:</strong> {formData.Default_Date?.label || '-'}</p><p><strong>Time in Default:</strong> {formData.Time_in_default?.label || '-'}</p><p><strong>Grade:</strong> {formData.Grade?.label || '(not provided)'}</p><p><strong>Calibration:</strong> {formData.Calibration_segment?.label || '(not provided)'}</p><p><strong>Status:</strong> {formData.Status?.label || '-'}</p><p><strong>Target:</strong> {formData.Target_variable?.label || '-'}</p><p><strong>MRP:</strong> {formData.MRP_column?.label || '-'}</p></div></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={runDescriptive} disabled={loadingStep === 'descriptive' || isViewMode} style={{ ...buttonStyle, background: loadingStep === 'descriptive' || isViewMode ? '#95a5a6' : '#1abc9c' }}>{loadingStep === 'descriptive' ? 'Running...' : 'Run Descriptive Analysis →'}</button></div>
      </div>)}

      {step === 2 && descriptiveData && (<div>
        <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}><div style={{ maxWidth: '250px' }}><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Filter by Grade</label><Select options={[{ value: 'all', label: 'All Grades' }, ...gradeOptions]} value={selectedGrade ? { value: selectedGrade, label: selectedGrade } : { value: 'all', label: 'All Grades' }} onChange={(opt) => setSelectedGrade(opt.value === 'all' ? null : opt.value)} styles={compactCs} menuPortalTarget={document.body} /></div></div>
        <ScatterPlot plotData={filteredDescriptiveData} defaultTitle="Recovery Rates by Time in Default" xlabel="Time in Default" ylabel="Recovery Rate" titleKey="descriptive" xColumn="time_in_default" yColumn="coefficient" colorColumn="year" />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}><button onClick={() => setStep(3)} disabled={isViewMode} style={{ ...buttonStyle, background: isViewMode ? '#95a5a6' : '#1abc9c' }}>Next: Retained →</button></div>
      </div>)}

      {step === 3 && (<div>
        <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}><h4 style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Exclusion Rules</h4><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Select Column</label><Select options={columnOptions} value={exclusionColumn} onChange={setExclusionColumn} isClearable styles={cs} menuPortalTarget={document.body} isDisabled={isViewMode}/></div><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Values to Exclude</label><Select options={exclusionOptions} value={exclusionValues} onChange={setExclusionValues} isMulti styles={cs} menuPortalTarget={document.body} isDisabled={!exclusionColumn || isViewMode} /></div></div><button onClick={runRetained} disabled={loadingStep === 'retained' || isViewMode} style={{ ...buttonStyle, background: loadingStep === 'retained' || isViewMode ? '#95a5a6' : '#1abc9c' }}>{loadingStep === 'retained' ? 'Calculating...' : 'Apply & Run →'}</button></div>
        {retainedResults && (<><ScatterPlot plotData={retainedScatterData} defaultTitle="Development Coefficients by Grade" xlabel="Time in Default" ylabel="Coefficient" titleKey="retained" xColumn="time_in_default" yColumn="coefficient" colorColumn="grade" />{retainedResults.table_data && <TableComponent data={retainedResults.table_data} title="Retained Coefficients Table" />}</>)}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}><button onClick={() => setStep(4)} disabled={!retainedResults} style={buttonStyle}>Next: Bootstrap →</button></div>
      </div>)}

      {step === 4 && (<div>
        <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}><h4 style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Bootstrap Settings</h4><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Iterations</label><input type="number" value={bootstrapIterations} onChange={e => setBootstrapIterations(parseInt(e.target.value))} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #e0e0e0', borderRadius: '4px' }} disabled={isViewMode}/></div><div><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Confidence Level (%)</label><input type="number" value={confidenceLevel} onChange={e => setConfidenceLevel(parseInt(e.target.value))} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #e0e0e0', borderRadius: '4px' }} disabled={isViewMode}/></div></div><button onClick={runBootstrap} disabled={loadingStep === 'bootstrap' || isViewMode} style={{ ...buttonStyle, background: loadingStep === 'bootstrap' || isViewMode ? '#95a5a6' : '#1abc9c' }}>{loadingStep === 'bootstrap' ? 'Running...' : 'Run Bootstrap →'}</button></div>
        {bootstrapResults && <ScatterPlot plotData={bootstrapScatterData} defaultTitle={`Bootstrap - ${confidenceLevel}th Percentile`} xlabel="Time in Default" ylabel="Coefficient" titleKey="bootstrap" xColumn="time_in_default" yColumn="coefficient" colorColumn="grade" />}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}><button onClick={() => setStep(5)} disabled={!bootstrapResults} style={buttonStyle}>Next: Projection →</button></div>
      </div>)}

      {step === 5 && (<div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}><button onClick={runProjection} disabled={loadingStep === 'projection' || isViewMode} style={{ ...buttonStyle, background: loadingStep === 'projection' || isViewMode ? '#95a5a6' : '#1abc9c' }}>{loadingStep === 'projection' ? 'Running...' : 'Run Projection →'}</button></div>
        {projectionResults && (<>
          <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}><div style={{ maxWidth: '250px' }}><label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Filter by Grade</label><Select options={[{ value: 'all', label: 'All Grades' }, ...gradeOptions]} value={selectedGradeProjection ? { value: selectedGradeProjection, label: selectedGradeProjection } : { value: 'all', label: 'All Grades' }} onChange={(opt) => setSelectedGradeProjection(opt.value === 'all' ? null : opt.value)} styles={compactCs} menuPortalTarget={document.body} /></div></div>
          <LineChart plotData={filteredTraces} defaultTitle="Observed vs Projected Recovery by Year" xlabel="Year of Default" ylabel="Cumulative Recovery Rate" titleKey="projection" />
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px', padding: '12px', background: '#f9f9f9', borderRadius: '8px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Mean Column Name</label><div style={{ display: 'flex', gap: '6px' }}><input type="text" value={saveColNameMean} onChange={e => setSaveColNameMean(e.target.value)} style={{ padding: '6px 8px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', width: '150px' }} disabled={isViewMode}/><button onClick={() => saveProjection('mean')} disabled={loadingStep === 'savemean' || isViewMode} style={{ ...buttonStyle, background: (loadingStep === 'savemean' || isViewMode) ? '#95a5a6' : '#27ae60', whiteSpace: 'nowrap', padding: '6px 12px', fontSize: '11px' }}>{loadingStep === 'savemean' ? '...' : '💾 Save'}</button></div></div>
              <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>{confidenceLevel}th Column Name</label><div style={{ display: 'flex', gap: '6px' }}><input type="text" value={saveColName75} onChange={e => setSaveColName75(e.target.value)} style={{ padding: '6px 8px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', width: '150px' }} disabled={isViewMode} /><button onClick={() => saveProjection('75th')} disabled={loadingStep === 'save75th' || isViewMode} style={{ ...buttonStyle, background: (loadingStep === 'save75th' || isViewMode) ? '#95a5a6' : '#e67e22', whiteSpace: 'nowrap', padding: '6px 12px', fontSize: '11px' }}>{loadingStep === 'save75th' ? '...' : '💾 Save'}</button></div></div>
            </div>
            {/*<button onClick={exportResults} style={{ ...buttonStyle, whiteSpace: 'nowrap' }}>📥 Export Results</button>*/}
          </div>
        </>)}

        {/* ========== ADDED: Comments Section ========== */}
        <div style={{ marginTop: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
          <div 
            style={{ 
              background: '#f5f7fa', 
              padding: '12px 16px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              fontWeight: 600,
              fontSize: '14px',
              color: '#2c3e50'
            }}
            onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
          >
            <span>💬 Comments & Discussions ({comments.length})</span>
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {isCommentsExpanded ? '−' : '+'}
            </span>
          </div>
          
          {isCommentsExpanded && (
            <div style={{ padding: '16px', background: 'white' }}>
              <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                {comments.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    No comments yet. Be the first to add a comment.
                  </Typography>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        padding: '10px 12px',
                        marginBottom: '8px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: '3px solid #1abc9c'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                          {comment.user_email || comment.user_id || 'Unknown'}
                        </span>
                        <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                        {comment.text}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment or question..."
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '13px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    resize: 'vertical',
                    minHeight: '60px',
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={savingComment || !newComment.trim()}
                  style={{
                    padding: '8px 20px',
                    background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    height: 'fit-content'
                  }}
                >
                  {savingComment ? 'Sending...' : 'Add Comment'}
                </button>
              </div>
              
              {!savedAnalysisId && comments.length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  💡 Comments will be permanently saved when you click "Save Analysis"
                </Typography>
              )}
            </div>
          )}
        </div>

        {/* ========== ADDED: Professional Action Area ========== */}
        <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <MuiSelect
                value={analysisStatus}
                onChange={(e) => setAnalysisStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="validated">Validated</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </MuiSelect>
            </FormControl>
          </div>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={analysisDescription}
            onChange={(e) => setAnalysisDescription(e.target.value)}
            placeholder="Describe the purpose, methodology, or key findings of this multiplicative projection analysis..."
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => setStep(0)}
              disabled={isViewMode}
              size="small"
            >
              New Analysis
            </Button>
            
            <Button
              variant="contained"
              onClick={exportResults}
              size="small"
              sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
            >
              📥 Export
            </Button>
            
            <Button
              variant="contained"
              onClick={handleSaveAnalysis}
              disabled={!projectionResults || isViewMode}
              size="small"
              sx={{ bgcolor: projectionResults && !isViewMode ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
            >
              💾 Save Analysis
            </Button>
          </Box>
        </div>
      </div>)}
    </div>
  );
};

export default MultiplicativeModel;